import { Box, FormControl, FormHelperText } from '@mui/material'
import { styled } from '@mui/material/styles'

import { useInputImage } from '#/components/FormCall/Inputs/InputImage/Logic/useInputImage'
import { InputImageProps } from '#/components/FormCall/Inputs/InputImage/types'
import { Header } from '#/components/FormCall/Inputs/InputImage/View/header'
import { Preview } from '#/components/FormCall/Inputs/InputImage/View/preview'
import { Selection } from '#/components/FormCall/Inputs/InputImage/View/selection'
import { CircularLoader } from '#/components/Loader/Circular'
import Colors from '#/styles/Colors'

type StateProps = {
  disabled?: boolean
  hover?: boolean
}

const Wrapper = styled('div', {
  shouldForwardProp: prop => !['disabled', 'hover'].includes(prop as string),
})<StateProps>(({ disabled, hover }) => ({
  ...(disabled && {
    backgroundColor: '#f1f1f1',
    opacity: 0.8,
    pointerEvents: 'none',
  }),
  alignItems: 'center',
  border: '2px dashed',
  borderColor: '#ccc',
  borderRadius: '8px',
  cursor: 'pointer',
  display: 'flex',
  flex: 1,
  flexDirection: 'column',
  height: '100%',
  justifyContent: 'center',
  minHeight: 100,
  overflow: 'hidden',
  position: 'relative',
  ...(hover && {
    borderColor: Colors.primary,
    color: Colors.primary,
  }),
}))

const InputImage = ({
  accept,
  description,
  disabled,
  error,
  label,
  onChange,
  output,
  required,
  value,
}: InputImageProps) => {
  const {
    actions,
    aspectLocked,
    crop,
    cropping,
    failed,
    file,
    fileInputRef,
    handleAction,
    handleDragOver,
    handleDrop,
    handleFileChange,
    handleMouseEnter,
    handleMouseLeave,
    hover,
    imageRef,
    loading,
    onCropChange,
    onCropComplete,
    onImageLoad,
    openFileDialog,
    preview,
  } = useInputImage({ accept, onChange, output, value })

  const renderContent = () => {
    if (loading) {
      return <CircularLoader minHeight={100} />
    }

    if (!preview && !failed) {
      return <Selection description={description} disabled={disabled} onClick={openFileDialog} />
    }

    return (
      <Preview
        actions={actions}
        aspectLocked={aspectLocked}
        crop={crop}
        cropping={cropping}
        disabled={disabled}
        failed={failed}
        file={file}
        imageRef={imageRef}
        onAction={handleAction}
        onCropChange={onCropChange}
        onCropComplete={onCropComplete}
        onImageLoad={onImageLoad}
        preview={preview}
      />
    )
  }

  return (
    <Box onDragOver={handleDragOver} onDrop={handleDrop}>
      <FormControl fullWidth error={Boolean(error)}>
        <Header label={label} required={required} error={error} />
        <Wrapper
          disabled={disabled}
          hover={hover}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {renderContent()}
        </Wrapper>
        <input
          type="file"
          ref={fileInputRef}
          accept={accept}
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />

        {error && <FormHelperText error>{error}</FormHelperText>}
      </FormControl>
    </Box>
  )
}

export default InputImage
